import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Progress,
  Typography,
  Space,
  Row,
  Col,
  Card,
  Result,
  Spin,
} from "antd";
import {
  ClockCircleOutlined,
  GiftOutlined,
  InstagramOutlined,
  CheckCircleTwoTone,
  FrownOutlined,
} from "@ant-design/icons";
import confetti from "canvas-confetti";
import callAPI from "../fetch/main";

const { Title, Text } = Typography;

export default function LegendProOfferModal({
  open,
  onClose,
  onClaim,
  isConnectionExpired,
}) {
  const TOTAL_SECONDS = 10 * 60; // 10 minutes
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [showResultModal, setShowResultModal] = useState(null); // 'loading' | 'success' | 'error'

  let scopes =
    "business_basic,business_content_publish,business_manage_comments,business_manage_messages";
  let appId = "996086435858506";
  let redirectURL = "https://app.instantdm.com/fb-rdr";
  let ig_url = `https://www.instagram.com/oauth/authorize?client_id=${appId}&redirect_uri=${redirectURL}&response_type=code&scope=${scopes}`;

  // countdown timer
  useEffect(() => {
    if (!open) return;
    setSecondsLeft(TOTAL_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  const mins = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const secs = String(secondsLeft % 60).padStart(2, "0");
  const progress = Math.round(
    ((TOTAL_SECONDS - secondsLeft) / TOTAL_SECONDS) * 100
  );

  const handleClaim = () => {
    if (secondsLeft === 0) return;

    if (isConnectionExpired) {
      setShowReconnectModal(true);
      return;
    }

    setLoading(true);
    setShowResultModal("loading");

    let apiURL = "/claim_one_month_free";
    callAPI({
      url: apiURL,
      method: "GET",
      loadingState: setLoading,
      dataState: (response) => {
        setData(response);

        if (response?.code === 404) {
          setShowResultModal("error");
        } else if (response?.code) {
  setShowResultModal("success");

  // Create a temporary fullscreen canvas for confetti
  const confettiCanvas = document.createElement("canvas");
  confettiCanvas.style.position = "fixed";
  confettiCanvas.style.top = "0";
  confettiCanvas.style.left = "0";
  confettiCanvas.style.width = "100%";
  confettiCanvas.style.height = "100%";
  confettiCanvas.style.pointerEvents = "none";
  confettiCanvas.style.zIndex = "999999"; // 🟢 top of all layers
  document.body.appendChild(confettiCanvas);

  const myConfetti = confetti.create(confettiCanvas, { resize: true });

  // fire confetti bursts
  myConfetti({
    particleCount: 180,
    spread: 80,
    origin: { y: 0.6 },
  });

  setTimeout(() => {
    myConfetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.4 },
    });
  }, 500);

  // remove canvas after animation ends
  setTimeout(() => {
    document.body.removeChild(confettiCanvas);
  }, 5000);
}
 else {
          setShowResultModal("error");
        }
      },
    });
  };

  return (
    <>
      {/* Main Offer Modal */}
      <Modal
        open={open}
        footer={null}
        centered
        closable={false}
        maskClosable={false}
        width={"600px"}
        bodyStyle={{ padding: 0, background: "#fff", borderRadius: 16 }}
      >
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            textAlign: "center",
            padding: 24,
            background: "linear-gradient(145deg, #ffffff, #f7f9ff)",
          }}
        >
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GiftOutlined style={{ fontSize: 48, color: "#722ed1" }} />
            </div>

            <Title level={3} style={{ marginBottom: 0 }}>
              🎉 Claim Your{" "}
              <span style={{ color: "#722ed1" }}>1 Month Legend Pro</span> For Free!
            </Title>

            <Text type="secondary" style={{ fontSize: 15 }}>
              We know you left InstantDM for some reason but we’d love to have
              you back! Grab your free access to <b>Legend Pro</b> for 1 month
              before it expires.
            </Text>

            <Row justify="center" align="middle" gutter={8}>
              <Col>
                <ClockCircleOutlined style={{ color: "#fa541c", fontSize: 20 }} />
              </Col>
              <Col>
                <Text strong style={{ fontSize: 16, color: "#fa541c" }}>
                  Offer expires in {mins}:{secs}
                </Text>
              </Col>
            </Row>

            <Progress
              percent={progress}
              status={secondsLeft === 0 ? "exception" : "active"}
              showInfo={false}
              strokeColor="#722ed1"
            />

            <Button
                type="primary"
                size="large"
                block
                style={{
                    borderRadius: 8,
                    background: "#722ed1",
                    height: "auto", // allow text wrapping height
                    fontSize: 16,
                    width: "100%",
                    whiteSpace: "normal", // ✅ allows text to break
                    wordBreak: "break-word", // ✅ prevents overflow on small screens
                    lineHeight: 1.3, // nicer spacing when text wraps
                    padding: "12px 16px", // balanced padding for wrapped text
                }}
                onClick={handleClaim}
                loading={loading}
                >
                Claim One Month Legend Pro Now
                </Button>

            <Text type="secondary" style={{ fontSize: 12 }}>
              *Limited-time offer — only available for the next 10 minutes.
            </Text>
          </Space>
        </Card>
      </Modal>

      {/* Reconnect Instagram Modal */}
      <Modal
        open={showReconnectModal}
        onCancel={() => setShowReconnectModal(false)}
        centered
        footer={null}
        width={400}
      >
        <Result
          icon={<InstagramOutlined style={{ color: "#E1306C" }} />}
          title="Connect Instagram to claim this offer"
          subTitle="To claim your free Legend Pro offer, please reconnect your Instagram account."
          extra={
            <Button
              type="primary"
              href={ig_url}
              onClick={() => setShowReconnectModal(false)}
              style={{
                background: "#E1306C",
                borderColor: "#E1306C",
                borderRadius: 6,
              }}
            >
              Connect Instagram
            </Button>
          }
        />
      </Modal>

      {/* Claiming in Progress */}
      <Modal
        open={showResultModal === "loading"}
        footer={null}
        closable={false}
        centered
      >
        <Result
          icon={<Spin size="large" />}
          title="Claiming your offer..."
          subTitle="Please wait while we activate your free Legend Pro month."
        />
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showResultModal === "success"}
        footer={null}
        centered
        onCancel={() => window.location.reload()}
      >
        <Result
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          title="Offer Claimed Successfully!"
          subTitle="You now have 1 month of Legend Pro enjoy your premium access!"
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Continue to Dashboard
            </Button>
          }
        />
      </Modal>

      {/* Error Modal */}
      <Modal
        open={showResultModal === "error"}
        footer={null}
        centered
        onCancel={() => setShowResultModal(null)}
      >
        <Result
          icon={<FrownOutlined style={{ color: "#fa541c" }} />}
          title="Offer Not Found"
          subTitle="This free offer has expired or was already claimed."
          extra={
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          }
        />
      </Modal>
    </>
  );
}
